// Lightweight logger utility for IntelliAuth frontend apps.
// In production builds, console.log/debug/info calls are stripped
// by esbuild's `pure` option in vite.config.ts — zero bundle cost.

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerOptions {
  component: string
}

interface Logger {
  debug: (message: string, context?: Record<string, unknown>) => void
  info: (message: string, context?: Record<string, unknown>) => void
  warn: (message: string, context?: Record<string, unknown>) => void
  error: (message: string, context?: Record<string, unknown>) => void
}

function formatMessage(level: LogLevel, component: string, message: string): string {
  return `[${level.toUpperCase()}] [${component}] ${message}`
}

function createLogFn(
  level: LogLevel,
  component: string,
  consoleFn: (...args: unknown[]) => void,
): (message: string, context?: Record<string, unknown>) => void {
  return (message: string, context?: Record<string, unknown>) => {
    if (context) {
      consoleFn(formatMessage(level, component, message), context)
    } else {
      consoleFn(formatMessage(level, component, message))
    }
  }
}

/** Create a logger scoped to a component name. */
export function createLogger(options: LoggerOptions): Logger {
  const { component } = options
  return {
    debug: createLogFn('debug', component, console.debug),
    info: createLogFn('info', component, console.info),
    warn: createLogFn('warn', component, console.warn),
    error: createLogFn('error', component, console.error),
  }
}

/** Root logger instance for general use. */
export const logger = createLogger({ component: 'App' })
